import type { ReactNode } from 'react'

import Tooltip from '~/components/Tooltip'
import i18n from '~/i18n'
import { formatRoasCutoffDate } from '~/modules/retailMedia/constants'

import { MetricTooltip } from '../../MetricMethodologyTooltip'

/** Métricas cujo tooltip rico tem link "saiba mais" (central de ajuda). */
const LEARN_MORE_LINK_KEY: Record<string, string> = {
  roas: 'rm:alerts.roasUpdate.link',
  conversion_rate: 'rm:alerts.conversionUpdate.link'
}

interface TitleWithTooltipProps {
  /** Chave da métrica em i18n (ex.: 'cost', 'roas', 'conversion_rate'). */
  metric: string
  /**
   * Slug alternativo para as chaves de tooltip quando a mesma métrica tem
   * metodologia diferente no contexto (ex.: 'conversionsObjectiveBased' em
   * campanhas por objetivo). O rótulo continua vindo de `metric`.
   */
  tooltipMetric?: string
  /**
   * Ícone de alerta a exibir ao lado do título, se algum. Cada chamador
   * decide qual alerta (e sob qual condição) faz sentido para a métrica que
   * está montando.
   */
  alertIcon?: ReactNode
  /** Valores para interpolação do tooltip (ex.: { months: lookbackMonths }). */
  tooltipValues?: Record<string, unknown>
  /**
   * Texto exibido no card (gatilho do tooltip). Quando omitido, usa o mesmo
   * texto do tooltip (rm:metrics.{metric}). Útil para cards de percentual,
   * cujo ícone já é um "%": evita repetir o símbolo no título do card.
   */
  cardLabel?: string
}

export const TitleWithTooltip = ({
  metric,
  tooltipMetric,
  alertIcon,
  tooltipValues,
  cardLabel
}: TitleWithTooltipProps) => {
  // Chaves dinâmicas: o `t` tipado não aceita template string.
  const translate = i18n.t as unknown as (
    key: string,
    options?: Record<string, unknown>
  ) => string
  const tooltipKey = tooltipMetric ?? metric
  const label = translate(`rm:metrics.${metric}`)
  const visibleLabel = cardLabel ?? label
  const tooltipTitleKey = `rm:metrics.${tooltipKey}.tooltip.title`
  const tooltipTitleLegacyKey = `rm:metrics.${tooltipKey}.tooltipTitle`
  let tooltipTitle = label

  if (i18n.exists(tooltipTitleKey)) {
    tooltipTitle = translate(tooltipTitleKey)
  } else if (i18n.exists(tooltipTitleLegacyKey)) {
    tooltipTitle = translate(tooltipTitleLegacyKey)
  }
  const hasRichTooltip = i18n.exists(
    `rm:metrics.${tooltipKey}.tooltip.description`
  )
  const hasSimpleTooltip = i18n.exists(`rm:metrics.${tooltipKey}.tooltip`)
  const learnMoreKey = LEARN_MORE_LINK_KEY[tooltipKey]
  const cutoffDate = formatRoasCutoffDate(i18n.language)

  // Sem tooltip definido: exibe apenas o rótulo.
  if (!hasRichTooltip && !hasSimpleTooltip) {
    return (
      <span
        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
      >
        {visibleLabel}
        {alertIcon}
      </span>
    )
  }

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
    >
      <Tooltip
        noMargin
        title={tooltipTitle}
        text={
          hasRichTooltip ? (
            <MetricTooltip
              metric={tooltipKey}
              values={{ date: cutoffDate, ...tooltipValues }}
              learnMoreHref={learnMoreKey ? translate(learnMoreKey) : undefined}
            />
          ) : (
            translate(`rm:metrics.${tooltipKey}.tooltip`, tooltipValues)
          )
        }
      >
        {visibleLabel}
      </Tooltip>
      {alertIcon}
    </span>
  )
}
